import EditorialImage from 'components/EditorialImage';
import { contentApiUrl } from 'services/client/editorial';
import React from 'react';
import Markdoc from '@markdoc/markdoc';
import { renderTree } from 'utils/shared/editorialMarkdoc';
import type { PortableTextComponents } from '@portabletext/react';
import { renderPortablePresentation } from './portablePresentation';
import { newsComponents } from './newsComponents';
import NewsLetterForm from 'components/Website/NewsLetter/Form';
import {
  emailMarkdocComponents,
  linkEmailAddressesInTree,
  renderEmailAddresses,
} from 'components/EmailLink/richText';

interface Props {
  body: string;
  baseUrl: string;
  onImageError?: () => void;
  variant?: 'news';
  portableComponents?: PortableTextComponents;
}

export default function EditorialBody({
  body,
  baseUrl,
  onImageError,
  variant,
  portableComponents,
}: Props) {
  const tree = linkEmailAddressesInTree(renderTree(body));
  const imageUrl = (src: string) =>
    src.startsWith('/api/content/media?')
      ? contentApiUrl(src.slice('/api/content/'.length))
      : new URL(src, baseUrl).toString();
  if (variant === 'news' || portableComponents)
    return (
      <>
        {renderPortablePresentation(
          tree,
          variant === 'news' ? newsComponents : portableComponents!,
          imageUrl,
          onImageError,
        )}
      </>
    );
  return (
    <>
      {Markdoc.renderers.react(
        Markdoc.Tag.isTag(tree) && tree.name === 'article' ? tree.children : tree,
        React,
        {
          components: {
            ContentImage: ({ src, ...props }: any) => (
              <EditorialImage {...props} onError={onImageError} src={imageUrl(src)} />
            ),
            ContentTable: ({ rows, caption }: { rows: string; caption?: string }) => (
              <table>
                {caption && <caption>{renderEmailAddresses(caption)}</caption>}
                <tbody>
                  {(JSON.parse(rows) as string[][]).map((row, index) => (
                    <tr key={index}>
                      {row.map((cell, column) =>
                        index === 0 ? (
                          <th key={column}>{renderEmailAddresses(cell)}</th>
                        ) : (
                          <td key={column}>{renderEmailAddresses(cell)}</td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ),
            NewsletterCta: () => <NewsLetterForm />,
            SourceCenter: ({ children }: { children?: React.ReactNode }) => (
              <div className="text-center">{children}</div>
            ),
            SourceHighlight: ({ children }: { children?: React.ReactNode }) => (
              <span className="font-bold text-blue-500">{children}</span>
            ),
            SourceStrike: ({ children }: { children?: React.ReactNode }) => <del>{children}</del>,
            SourceInternalLink: ({ children }: { children?: React.ReactNode }) => (
              <span>{children}</span>
            ),
            ...emailMarkdocComponents,
          },
        },
      )}
    </>
  );
}
